import { useCallback, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import {
  getNowPlaying,
  sendSessionCommand,
  terminateSession,
  type NowPlayingSession,
  type SessionCommandAction,
} from "../../../lib/dashboardAdmin";
import { ApiProblemError } from "../../../lib/authManager";
import { useServers } from "../../../lib/servers";
import { useNavServers } from "../../../lib/navServers";
import { useLiveMetrics } from "../../../lib/useLiveMetrics";
import { METRICS_WINDOW_MS } from "../../../lib/metricsBuffer";
import { formatBitrateFromBytes } from "../../../lib/mediaFormat";
import { Chip, ErrorNote, Spinner } from "../../../components/ui";
import {
  SheetHeader,
  SheetInset,
  SheetNotice,
  SheetSection,
} from "../../../components/settings-sheet";
import { ConfirmDialog } from "../../../components/modal";
import { TimeSeriesChart } from "../../../components/charts";
import { PlayIcon } from "../../../components/icons";
import { EmptyState } from "../../../components/page-state";
import { SessionCard } from "../../../components/dashboard/session-card";
import { InProgressSection } from "../../../components/dashboard/in-progress";
import { MessageDialog } from "../../../components/dashboard/message-dialog";
import { ServerSettingsGuard } from "../../../components/server-settings";

export const Route = createFileRoute("/_authed/settings/server/$serverId/dashboard")({
  component: ServerDashboard,
});

const POLL_MS = 5_000;

const CHART_1 = "var(--rago-chart-1)"; // slot 1: rago-server / Local
const CHART_2 = "var(--rago-chart-2)"; // slot 2: system / Remote

function fmtPct(v: number): string {
  return `${Math.round(v)}%`;
}

function is404(err: unknown): boolean {
  return err instanceof ApiProblemError && err.problem.status === 404;
}

function ServerDashboard() {
  const { serverId } = Route.useParams();
  // Subscribe to the registry: getSource() is a sync snapshot, and the
  // queries would stay disabled forever without the hydration rerender.
  const { sources } = useSources();
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;
  const server = isServer ? (source as ServerSource) : null;
  // Broker-offline: the guard swaps the page for the unavailable screen —
  // idle the socket lease and both pollers too, or their doomed fetches
  // re-race the dead server's candidates every second behind it.
  const { servers } = useServers();
  const online = servers.find((s) => s.id === serverId)?.online ?? false;
  const qc = useQueryClient();
  const npKey = ["dashboard", "now-playing", serverId];
  // Library titles for the In progress rows (queued scans carry none).
  const nav = useNavServers(sources);
  const navData = nav.data;
  const libraryTitle = useCallback(
    (libraryId: string) =>
      navData?.find((s) => s.id === serverId)?.libraries.find((l) => l.id === libraryId)?.title,
    [navData, serverId],
  );

  // Metrics stream live over the events socket (2-minute window,
  // backfilled on open); the hook forwards everything else here —
  // session.state and the media.* transitions refetch the cards (the
  // latter are the only live signal for Direct Play), and
  // session.progress patches transcode telemetry straight into the cached
  // cards so throttle badges and encoder bars tick at 1 Hz between fetches.
  const metrics = useLiveMetrics({
    serverId,
    source: online ? server : null,
    onEvent: (ev) => {
      if (ev.snapshot) return;
      if (ev.type === "session.state" || ev.type.startsWith("media.")) {
        void qc.invalidateQueries({ queryKey: npKey });
        return;
      }
      if (ev.type === "session.progress" && ev.data) {
        const d = ev.data as {
          sessionId?: string;
          throttled?: boolean;
          progressSeg?: number;
          producedSeg?: number;
          segCount?: number;
          aheadSec?: number;
          hw?: boolean;
          hwTier?: string;
          hwDevice?: string;
          swFallback?: boolean;
        };
        if (!d.sessionId) return;
        // Patch, never refetch: only the transcode fields move — the
        // card's play-state comes from timeline beats, not the encoder.
        // The hardware facts ride along so a mid-session GPU demotion
        // flips the HW chip without waiting for a poll; a server that
        // omits them (older build) leaves the polled values alone.
        qc.setQueryData<{ items: NowPlayingSession[] }>(npKey, (cur) =>
          cur
            ? {
                items: cur.items.map((it) =>
                  it.sessionId === d.sessionId
                    ? {
                        ...it,
                        throttled: d.throttled ?? it.throttled,
                        progressSeg: d.progressSeg ?? it.progressSeg,
                        producedSeg: d.producedSeg ?? it.producedSeg,
                        segCount: d.segCount ?? it.segCount,
                        aheadSec: d.aheadSec ?? it.aheadSec,
                        ...(d.hw != null
                          ? { hw: d.hw, hwTier: d.hwTier, hwDevice: d.hwDevice }
                          : {}),
                        swFallback: d.swFallback ?? it.swFallback,
                      }
                    : it,
                ),
              }
            : cur,
        );
      }
    },
  });

  // Events are the primary signal now; the poll is a slow safety net
  // while the socket is up (positions drift between 10 s beats) and the
  // old 5 s cadence only when it is down. Background-tab polling stays —
  // that's where a dashboard usually lives while the admin works.
  const nowPlaying = useQuery({
    queryKey: npKey,
    enabled: isServer && online,
    refetchInterval: metrics.live ? 30_000 : POLL_MS,
    refetchIntervalInBackground: true,
    queryFn: () => getNowPlaying((server as ServerSource).conn),
  });

  // ---- session commands (route-owned so dialogs outlive card rerenders) ----
  const [pendingCommand, setPendingCommand] = useState<string | null>(null);
  const [killTarget, setKillTarget] = useState<NowPlayingSession | null>(null);
  const [killMessage, setKillMessage] = useState("");
  const [killBusy, setKillBusy] = useState(false);
  const [killError, setKillError] = useState<unknown>(null);
  const [msgTarget, setMsgTarget] = useState<NowPlayingSession | null>(null);
  const [msgBusy, setMsgBusy] = useState(false);
  const [msgError, setMsgError] = useState<unknown>(null);

  async function command(s: NowPlayingSession, action: SessionCommandAction, message?: string) {
    if (!server) return;
    await sendSessionCommand(server.conn, s.id, action, message);
  }

  async function playPause(s: NowPlayingSession, action: "pause" | "play") {
    if (!server) return;
    setPendingCommand(s.id);
    // Optimistic flip; the next timeline beat (or an error refetch) settles it.
    qc.setQueryData<{ items: NowPlayingSession[] }>(npKey, (data) =>
      data
        ? {
            items: data.items.map((it) =>
              it.id === s.id ? { ...it, state: action === "pause" ? "paused" : "playing" } : it,
            ),
          }
        : data,
    );
    try {
      await command(s, action);
    } catch {
      void qc.invalidateQueries({ queryKey: npKey });
    } finally {
      setPendingCommand(null);
    }
  }

  async function sendMessage(text: string) {
    if (!msgTarget) return;
    setMsgBusy(true);
    setMsgError(null);
    try {
      await command(msgTarget, "message", text);
      setMsgTarget(null);
    } catch (e) {
      setMsgError(e);
    } finally {
      setMsgBusy(false);
    }
  }

  async function confirmKill() {
    if (!server || !killTarget) return;
    setKillBusy(true);
    setKillError(null);
    const msg = killMessage.trim() || undefined;
    try {
      try {
        await command(killTarget, "stop", msg);
      } catch (e) {
        // Old server without the command endpoint: fall back to the HLS
        // session kill, which is all it supports anyway.
        if (is404(e) && killTarget.sessionId) {
          await terminateSession(server.conn, killTarget.sessionId, msg);
        } else {
          throw e;
        }
      }
      setKillTarget(null);
      setKillMessage("");
      void qc.invalidateQueries({ queryKey: npKey });
    } catch (e) {
      setKillError(e);
    } finally {
      setKillBusy(false);
    }
  }

  const unsupported = is404(nowPlaying.error);
  const sessions = nowPlaying.data?.items ?? [];
  const windowMs = METRICS_WINDOW_MS;

  // Chart inputs are memoized off the sample arrays — the chart memoizes
  // its geometry on series identity, so these must be stable per update.
  const bandwidthSeries = useMemo(
    () => [
      {
        label: "Local",
        color: CHART_1,
        points: metrics.bandwidth.map((p) => ({ t: p.at, v: p.lanBps })),
      },
      {
        label: "Remote",
        color: CHART_2,
        points: metrics.bandwidth.map((p) => ({ t: p.at, v: p.wanBps })),
      },
    ],
    [metrics.bandwidth],
  );
  const cpuSeries = useMemo(
    () => [
      {
        label: "rago-server",
        color: CHART_1,
        points: metrics.system.map((p) => ({ t: p.at, v: p.cpuProc })),
      },
      {
        label: "System",
        color: CHART_2,
        points: metrics.system.map((p) => ({ t: p.at, v: p.cpuHost })),
      },
    ],
    [metrics.system],
  );
  const memSeries = useMemo(
    () => [
      {
        label: "rago-server",
        color: CHART_1,
        points: metrics.system.map((p) => ({ t: p.at, v: p.memProc })),
      },
      {
        label: "System",
        color: CHART_2,
        points: metrics.system.map((p) => ({ t: p.at, v: p.memHost })),
      },
    ],
    [metrics.system],
  );

  const cpuSupported = metrics.system.some((s) => s.cpuProc !== null || s.cpuHost !== null);
  const memSupported = metrics.system.some((s) => s.memProc !== null || s.memHost !== null);

  return (
    <ServerSettingsGuard serverId={serverId} heading="Dashboard">
      {(guarded) => (
        <div>
          <SheetHeader
            title="Dashboard"
            action={
              unsupported ? null : (
                <Chip tone={metrics.live ? "ok" : "muted"}>{metrics.live ? "Live" : "Polling"}</Chip>
              )
            }
          />

          {unsupported ? (
            <SheetNotice title="Status">
              This server doesn't have the dashboard yet — update rago-server.
            </SheetNotice>
          ) : (
            <>
              {nowPlaying.error || metrics.error ? (
                <SheetInset>
                  <ErrorNote error={nowPlaying.error ?? metrics.error} />
                </SheetInset>
              ) : null}

              <InProgressSection
                serverId={serverId}
                source={online ? server : null}
                libraryTitle={libraryTitle}
              />

              <SheetSection title="Now playing">
                {nowPlaying.isLoading ? (
                  <p className="flex items-center gap-2 text-sm text-[var(--rago-text-2)]">
                    <Spinner /> Loading…
                  </p>
                ) : sessions.length === 0 ? (
                  <EmptyState compact icon={<PlayIcon size={22} />} title="Nothing is playing">
                    Sessions appear here the moment someone presses play.
                  </EmptyState>
                ) : (
                  // Column count tracks the sheet's width, not the viewport.
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4">
                    {sessions.map((s) => (
                      <SessionCard
                        key={s.id}
                        session={s}
                        source={server as ServerSource}
                        commandBusy={pendingCommand === s.id}
                        onPlayPause={(action) => void playPause(s, action)}
                        onMessage={() => {
                          setMsgError(null);
                          setMsgTarget(s);
                        }}
                        onStop={() => {
                          setKillError(null);
                          setKillMessage("");
                          setKillTarget(s);
                        }}
                      />
                    ))}
                  </div>
                )}
              </SheetSection>

              <SheetSection title="Bandwidth">
                {metrics.bandwidth.length === 0 ? (
                  <NoSamples loading={metrics.loading} />
                ) : (
                  <TimeSeriesChart
                    windowMs={windowMs}
                    total
                    yFloor={125_000} // 1 Mbps in bytes/sec — idle charts must not autoscale to noise
                    label="Served bandwidth over the last 2 minutes"
                    formatValue={formatBitrateFromBytes}
                    series={bandwidthSeries}
                  />
                )}
              </SheetSection>

              <SheetSection title="CPU">
                {metrics.system.length === 0 || !cpuSupported ? (
                  <NoSamples loading={metrics.loading} unsupported={metrics.system.length > 0} />
                ) : (
                  <TimeSeriesChart
                    windowMs={windowMs}
                    yCeil={100}
                    label="CPU usage over the last 2 minutes"
                    formatValue={fmtPct}
                    series={cpuSeries}
                  />
                )}
              </SheetSection>

              <SheetSection title="Memory">
                {metrics.system.length === 0 || !memSupported ? (
                  <NoSamples loading={metrics.loading} unsupported={metrics.system.length > 0} />
                ) : (
                  <TimeSeriesChart
                    windowMs={windowMs}
                    yCeil={100}
                    label="Memory usage over the last 2 minutes"
                    formatValue={fmtPct}
                    series={memSeries}
                  />
                )}
              </SheetSection>

              <ConfirmDialog
                open={killTarget !== null}
                title="Stop this stream?"
                body={
                  <div>
                    <p>
                      {killTarget?.userName ?? killTarget?.userId} is watching{" "}
                      <span className="text-[var(--rago-text-1)]">{killTarget?.title}</span>. Their
                      playback stops immediately.
                    </p>
                    <label
                      className="mt-4 block text-xs text-[var(--rago-text-3)]"
                      htmlFor="kill-message"
                    >
                      Message to the viewer (optional)
                    </label>
                    <input
                      id="kill-message"
                      className="rago-input mt-1 w-full"
                      placeholder="The server is going down for maintenance"
                      value={killMessage}
                      onChange={(e) => setKillMessage(e.target.value)}
                    />
                  </div>
                }
                confirmLabel="Stop stream"
                danger
                busy={killBusy}
                error={killError}
                onConfirm={() => void confirmKill()}
                onCancel={() => setKillTarget(null)}
              />
              <MessageDialog
                open={msgTarget !== null}
                recipient={msgTarget?.userName ?? msgTarget?.userId ?? ""}
                busy={msgBusy}
                error={msgError}
                onSend={(text) => void sendMessage(text)}
                onCancel={() => setMsgTarget(null)}
              />
            </>
          )}
        </div>
      )}
    </ServerSettingsGuard>
  );
}

function NoSamples({ loading, unsupported }: { loading: boolean; unsupported?: boolean }) {
  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-[var(--rago-text-2)]">
        <Spinner /> Loading…
      </p>
    );
  }
  return (
    <p className="text-sm text-[var(--rago-text-3)]">
      {unsupported ? "Not available on this platform." : "No samples yet — they arrive every few seconds."}
    </p>
  );
}
